import { get } from 'lodash-es';

import { Form, FormFeedback, FormGroup, Icon, Label, Tooltip } from '../index';
import type { IOptionItem } from '@wyz/types';
import { useFormContext } from 'react-hook-form';
import * as React from 'react';
import clsx from 'clsx';

type RadioOption = IOptionItem & { disabled?: boolean; helperText?: string };
type RadioGroupFieldInputProps<TKeys extends string> = {
  name: string;
  helperText?: string;
  label?: React.ReactNode;
  required?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  options: RadioOption[];
  renderOptionLabel?: (option: RadioOption) => React.ReactNode;
  translate: (key: TKeys) => string;
  direction?: 'vertical' | 'horizontal';
};
function RadioGroupField<TKeys extends string>({
  name,
  label,
  options,
  helperText,
  required,
  disabled,
  translate,
  direction = 'horizontal',
  renderOptionLabel,
}: RadioGroupFieldInputProps<TKeys>) {
  const {
    formState: { errors },
    register,
  } = useFormContext();
  const errorMessage = get(errors, [...name.split('.'), 'message']) as TKeys;
  const registered = register(name);
  return (
    <FormGroup>
      {label && (
        <Label className={`${required ? 'required' : ''}`}>{label}</Label>
      )}
      <>
        <div
          className={clsx('d-flex flex-wrap', {
            'flex-row space-x-4': direction === 'horizontal',
            'flex-column space-y-4': direction === 'vertical',
          })}
        >
          {options.map((item) => {
            const optionDisabled = item.disabled || disabled;
            const optionId = `${name}.${String(item.label)}`;
            return (
              <Form.Check
                key={item.value}
                type='radio'
                className={'form-check d-flex align-items-center space-x-2'}
                id={optionId}
              >
                <Form.Check.Input
                  id={optionId}
                  value={item.value}
                  disabled={optionDisabled}
                  className='mt-0'
                  type='radio'
                  isInvalid={!!errorMessage}
                  {...registered}
                  data-cy={optionId}
                />
                <Form.Check.Label htmlFor={optionId} className='mb-0 d-block'>
                  {renderOptionLabel ? renderOptionLabel(item) : item.label}
                </Form.Check.Label>
                {item.helperText && (
                  <Tooltip label={item.helperText}>
                    <Icon icon='info-circle' size='xxs' />
                  </Tooltip>
                )}
              </Form.Check>
            );
          })}
        </div>
        {errorMessage ? (
          <FormFeedback invalid>{translate(errorMessage)}</FormFeedback>
        ) : helperText ? (
          <FormFeedback>{helperText}</FormFeedback>
        ) : null}
      </>
    </FormGroup>
  );
}

export default RadioGroupField;
